import {
  BASE_URL,
  BASE_AUTH_URL,
  DISPATCH_BASE_URL,
  moduleTypes,
  ADMIN_URL,
  LAUNCH_LOGIN_URL,
  REPORT_BASE_URL,
  REPORTS_URL
} from './constants';
import {getAccessToken, setAccessToken} from '../actions/authenticateActions';
import {getRefreshToken, clearSessionData, setDataToLaunchStore} from './customMethods';

const axios = require('axios');

let isAlreadyFetchingAccessToken = false;
let subscribers = [];

// Track failed refresh attempts to prevent infinite loops
let failedRefreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 1;

const config = {
  headers: { 'Authorization': 'Basic ' + btoa('my-trusted-client:secret')},
  responseType: 'json'
};

export const getAxiosInstance = (type) => {
  const instance = axios.create({
    baseURL: getBaseUrl(type)
  });

  // Request interceptor to add token to requests
  instance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        // Add token to headers instead of URL for better security
        config.headers['Authorization'] = `Bearer ${token}`;
        
        // Remove token from URL if it exists (for backward compatibility)
        if (config.url && config.url.includes('access_token=')) {
          config.url = config.url.replace(/[?&]access_token=[^&]*/g, '');
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for handling 401 errors
  instance.interceptors.response.use(
    (response) => {
      // Reset failed attempts on successful response
      failedRefreshAttempts = 0;
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 401 and we haven't already tried to refresh for this request
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Check if we've exceeded max refresh attempts
        if (failedRefreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          clearSessionData();
          window.location.href = LAUNCH_LOGIN_URL;
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessToken();
          
          // Update the authorization header for the original request
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Remove access_token from URL if present
          if (originalRequest.url && originalRequest.url.includes('access_token=')) {
            originalRequest.url = originalRequest.url.replace(/[?&]access_token=[^&]*/g, '');
          }

          // Reset failed attempts on successful refresh
          failedRefreshAttempts = 0;
          
          // Retry the original request with new token
          return instance(originalRequest);
        } catch (refreshError) {
          failedRefreshAttempts++;
          clearSessionData();
          window.location.href = LAUNCH_LOGIN_URL;
          return Promise.reject(refreshError);
        }
      }

      // For non-401 errors or if refresh failed, reject the promise
      return Promise.reject(error);
    }
  );

  return instance;
};

const refreshAccessToken = async () => {
  // If already fetching token, wait for it
  if (isAlreadyFetchingAccessToken) {
    return new Promise((resolve) => {
      addSubscriber((token) => {
        resolve(token);
      });
    });
  }

  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  isAlreadyFetchingAccessToken = true;

  try {
    const response = await axios.post(
      `${getBaseUrl(moduleTypes.SERVICE)}oauth/token?grant_type=refresh_token&refresh_token=${refreshToken}`,
      '',
      config
    );

    if (!response.data || !response.data.access_token) {
      throw new Error('Invalid refresh token response');
    }

    const newToken = response.data.access_token;
    
    // Store the new token
    setAccessToken(response.data);
    
    // Notify all subscribers
    onAccessTokenFetched(newToken);
    
    return newToken;
  } catch (error) {
    // Clear the fetching flag on error
    isAlreadyFetchingAccessToken = false;
    subscribers = [];
    
    throw error;
  } finally {
    // Always reset the flag after attempt
    isAlreadyFetchingAccessToken = false;
  }
};

const onAccessTokenFetched = (access_token) => {
  // Notify all waiting requests
  subscribers.forEach(callback => callback(access_token));
  subscribers = [];
};

const addSubscriber = (callback) => {
  subscribers.push(callback);
};

const getBaseUrl = (type) => {
  switch(type) {
    case moduleTypes.DISPATCH:
      return DISPATCH_BASE_URL;
    case moduleTypes.SERVICE:
      return BASE_AUTH_URL;
    case moduleTypes.MANAGE:
      return BASE_URL;
    case moduleTypes.ADMIN:
      return ADMIN_URL;
    case moduleTypes.REPORTS:
      return REPORT_BASE_URL;
    case moduleTypes.REPORT:
      return REPORTS_URL;
    default:
      return BASE_URL;
  }
};

export const authenticate = async (userName, password, subDomain) => {
  const reqBody = {
    "userName": userName,
    "password": password,
    "subdomain": subDomain,
    "clientId": "my-trusted-client",
    "secretId": "secret"
  };

  try {
    const resp = await axios.post(
      `${getBaseUrl(moduleTypes.SERVICE)}userAuthService/loginUser`,
      reqBody,
      config
    );

    if (resp.status === 200) {
      if (resp.data.statusCode === 200) {
        const data = resp.data;
        data.isAuthenticated = true;
        failedRefreshAttempts = 0; // Reset on successful auth
        return data;
      } else {
        const data = resp.data;
        data.isAuthenticated = false;
        return data;
      }
    } else {
      return {isAuthenticated: false};
    }
  } catch(ex) {
    return {isAuthenticated: false};
  }
};

export const getAccessLevels = async (empId, companyId) => {
  // Note: Removed access_token parameter since it's now handled by interceptor
  const url = `employee/select/employeeAccessLevel?empId=${empId}&companyId=${companyId}`;
  
  try {
    const response = await getAxiosInstance(moduleTypes.ADMIN).get(url);
    
    if (response.status === 200) {
      setDataToLaunchStore('accessLevel', response.data.employeeAccess || {});
      setDataToLaunchStore('dashboardAccess', response.data.dashboardAccess || {});
    } else {
      setDataToLaunchStore('accessLevel', {});
      setDataToLaunchStore('dashboardAccess', {});
    }
  } catch(error) {
    setDataToLaunchStore('accessLevel', {});
    setDataToLaunchStore('dashboardAccess', {});
  }
};